/** @format */

import moment from 'moment';

import { TStore } from '../../redux/helpers/createStore';
import { TRoom } from '../room/declarations';
import { KEY_STEP_CHECK_DATES, KEY_STEP_PAYMENT_SUMMARY } from './constants';

// #region ::: DECLARATIONS

export interface TEvaluedExitWindows {
  startDate: string;
  endDate: string;
  evaluedPrice: number;
  ranking: number;
}

export type ContractualConditions = {
  adminFeeIn: number;
  adminFeeOut: number;
  cancellationMonthNumber: number;
  cityId: string;
  code: string;
  contractModel: 'CNTIND' | 'CNTDET';
  contractualConditionId: 'e234900b-adec-ec11-bb3d-000d3adc5e94';
  durationFrom: number;
  durationTo: number;
  legalContractType: 'Type5';
  legalEntityId: string;
  name: string;
  paymentOption: string;
  type: 'Uncertain';
};

export type CommercialConditions = {
  confirmationDeposit: number;
  depositAmount: number;
  depositMonthsNumber: number;
  discountPercentage: number;
  discountUntil: string;
  exitOptions: boolean;
  firstMonthRent: number;
  maxVacancyDays: number;
  minStayDays: number;
  preCleaningDays: number;
  priceVariationPercentage: number;
  price: number;
  commercialConditionsId: string;
  privateSale: boolean;
  discountedPrice: number;
};

export interface TReducerBookingOld {
  gtmStatus?: {
    slide1?: true;
    slide2?: true;
    slide3?: true;
    slide4?: true;
  };
  currentStep?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 11;
  nextButtonEnabled?: boolean;
  paymentIntent?: {
    state: 'init' | 'loading' | 'error' | 'done';
    value: {
      tenantId: string;
      contractId: string;
      company: string;
      paymentIntent: string;
    };
  };
  isPaying?: true;
  formSend?: {
    state: 'loading' | 'error' | 'done';
    messages: Array<string>;
  };
  room?: TRoom;
  availability?: {
    price: number;
    discountedPrice?: number;
    discountUntil?: string;
    visibleOnline: boolean;
    availableFrom: string;
    availableTo: string;
    bedAvailabilityId: string;
    bedCode: string;
    checkInDates: Array<string>;
    negotiating: boolean;
    status: 'Free' | '';
    checkoutDateFrom: string;
    checkoutDateTo: string;
    minimumStay: number;
    salableOnline: boolean;
    defaultCheckoutDate: string;
    validCheckoutDates: Array<string>;
    evaluedExitWindows?: Array<TEvaluedExitWindows>;
  };
  form?: {
    address: string;
    addressNumber: string;
    amenities: {
      balcony: boolean;
      bathroom: boolean;
    };
    birthCity: string;
    birthDate: string;
    birthNationId: {
      label: string;
      value: string;
    };
    cityOfResidence: string;
    commercialPrivacy: boolean;
    confirmEmail: string;
    delegatedFiscalCode: boolean;
    email: string;
    floor: string;
    gender: {
      label: string;
      value: 'M' | 'F';
    };
    job: {
      label: string;
      value: string;
    };
    mandatoryDocuments: boolean;
    moveInDate: string;
    moveOutDate: string;
    name: string;
    middleName: string;
    nationId: {
      label: string;
      value: string;
      isEu: boolean;
      countryCode: string;
    };
    nationalityId: {
      label: string;
      value: string;
      isEu: boolean;
      countryCode: string;
    };
    paymentType: string;
    phoneNumber: string;
    prefix: {
      value: string;
      label: string;
    };
    privacy: boolean;
    mktPrivacy: boolean;
    province: {
      label: string;
      value: string;
    };
    surface: string;
    surname: string;
    taxCode: string;
    termsAndConditions: boolean;
    totalAmount: number;
    zipCode: string;
  };
  options?: {
    countries: Array<{
      value: string;
      label: string;
      countryCode: string;
      isEu: boolean;
    }>;

    genders: Array<{
      value: string;
      label: string;
    }>;
    prefixes: Array<{
      value: string;
      label: string;
    }>;
    professions: Array<{
      value: string;
      label: string;
    }>;
    provinces: Array<{
      value: string;
      label: string;
    }>;
  };
  contractualConditions?: ContractualConditions;
  commercialConditions?: CommercialConditions;
  paymentResult?: {
    id: string;
    object: string;
    amount: 5000;
    amount_details: {
      tip: {};
    };
    automatic_payment_methods: null;
    canceled_at: null;
    cancellation_reason: null;
    capture_method: string;
    client_secret: string;
    confirmation_method: string;
    created: number;
    currency: 'eur';
    description: string;
    last_payment_error: null;
    livemode: false;
    next_action: null;
    payment_method: string;
    payment_method_types: ['card'];
    processing: null;
    receipt_email: string;
    setup_future_usage: null;
    shipping: null;
    source: null;
    status: 'succeeded';
  };
}
// #endregion

export const selectorBooking = (store: TStore): TReducerBookingOld => store.bookingOld;

export const selectorIsLoadingConditions = (store: TStore) => store.general.isLoadingConditions || false;

export const selectorBookingStripeIntent = (store: TStore) => {
  const booking = selectorBooking(store);
  return booking.paymentIntent?.value?.paymentIntent;
};
export const selectorBookingCurrentStep = (store: TStore) => {
  const booking = selectorBooking(store);
  return booking.currentStep || 0;
};

export const selectorIsPaying = (store: TStore) => {
  const booking = selectorBooking(store);
  return booking.isPaying || false;
};

export const selectorIsBookingEnabled = (store: TStore): boolean => {
  const booking = selectorBooking(store);
  const step = selectorBookingCurrentStep(store);
  const isLoadingConditions = selectorIsLoadingConditions(store);
  if (isLoadingConditions) return false;
  const isAlwaysEnabled = step === KEY_STEP_PAYMENT_SUMMARY || step === KEY_STEP_CHECK_DATES;
  if (isAlwaysEnabled) return true;
  return booking.nextButtonEnabled || false;
};

export const selectorBookingAvailability = (store: TStore) => {
  const booking = selectorBooking(store);
  return booking?.availability;
};

export const selectorBookingRoom = (store: TStore): TRoom | undefined => {
  const booking = selectorBooking(store);
  return booking?.room;
};

export const selectorAcceptedVacancy = (store: TStore) => {
  const room = selectorBookingRoom(store);
  return room.commercialConditions?.maxVacancyDays;
};

export const selectorMinimumExitDate = (store: TStore) => {
  const room = selectorBookingRoom(store);
  return room.commercialConditions?.minStayDays;
};

export const selectorBookingFormOptions = (store: TStore) => {
  const booking = selectorBooking(store);
  return booking.options;
};

export const selectorBookingTags = (store: TStore): Array<string> => {
  const room = selectorBookingRoom(store);
  if (!room) return [];
  return room?.tags || [];
};

export const selectorAvailabilityCheckInDate = (store: TStore): string => {
  const availability = selectorBookingAvailability(store);
  if (availability && availability.checkInDates) return availability.checkInDates[0];
  return '';
};

export const selectorAvailabilityCheckOutDate = (store: TStore): string | null => {
  const availability = selectorBookingAvailability(store);
  if (availability && availability?.checkoutDateFrom) return availability.checkoutDateFrom;
  return null;
};

export const selectorIsRoomNotAvailable = (store: TStore): boolean => {
  const availability = selectorBookingAvailability(store);
  if (!availability) return true;
  return !availability.availableFrom || availability.negotiating;
};

export const selectorIsNotSalableOnline = (store: TStore): boolean => {
  const availability = selectorBookingAvailability(store);
  if (!availability) return true;
  return !availability.salableOnline;
};

export const selectorBookingForm = (store: TStore) => {
  const booking = selectorBooking(store);
  return booking.form;
};

export const selectorBookingAgreementIsVisible = (store: TStore) => {
  const bookingForm = selectorBookingForm(store);
  return !!bookingForm.nationalityId && !bookingForm.nationalityId?.isEu;
};

export const selectorBookingCheckInDate = (store: TStore) => {
  const availability = selectorBookingAvailability(store);
  const bookingForm = selectorBookingForm(store);
  if (!availability || !bookingForm) return '';
  return moment(bookingForm.moveInDate || availability.checkInDates[0]).format('DD MMM YYYY');
};

export const selectorBookingCheckOutDate = (store: TStore) => {
  const availability = selectorBookingAvailability(store);
  const bookingForm = selectorBookingForm(store);
  if (!availability || !bookingForm) return '';

  return moment(bookingForm.moveOutDate || availability.checkoutDateFrom).format('DD MMM YYYY');
};

export const selectorBookingContractualConditions = (store: TStore) => {
  const booking = selectorBooking(store);
  return booking.contractualConditions;
};

export const selectorBookingCommercialConditions = (store: TStore) => {
  const booking = selectorBooking(store);
  return booking.commercialConditions;
};

export const selectorBookingIsDelegated = (store: TStore) => {
  const bookingForm = selectorBookingForm(store);
  return bookingForm.nationalityId && !bookingForm.nationalityId.isEu;
};

export const selectorBookingErrorMessages = (store: TStore) => store.bookingOld.formSend?.messages;
